import { createSelector } from "reselect"
import { ReduxState, RateTable, NestedLaborRate, NestedEquipmentRate, NestedMaterialRate } from "../common/types"
import { flattenCustomFields, SerializedRateTable, SerializedLaborRate, SerializedEquipmentRate, SerializedMaterialRate } from "@rhumbix/rmbx_matching"
import { tEntitySelection } from "../cached-data/types"

/**
 * Selects the rate tables entity state from Redux.
 */
const selectRateTablesById = (state: ReduxState): tEntitySelection["rateTables"] => state.entities.rateTables?.objects ?? {}

/**
 * Selector to get the cached labor rates from Redux state.
 */
export const selectLaborRatesById = (state: ReduxState): tEntitySelection["laborRates"] => state.entities.laborRates?.objects ?? {}

/**
 * Transforms a LaborRate object from a cached RateTable's labor_rates field into the format
 * expected by the matching library.
 *
 * Uses {@link flattenCustomFields} from rmbx_matching to convert the nested custom_fields
 * structure (keyed by model type, then field name) into a flat CustomFieldCriterion[] array.
 */
const transformLaborRateForMatching = (laborRate: NestedLaborRate): SerializedLaborRate => {
    // Build modifier_custom_ids from custom_modifier_* fields
    const modifierCustomIds: Record<string, number[]> = {}

    // Extract any custom_modifier_* fields
    Object.entries(laborRate).forEach(([key, value]) => {
        if (key.startsWith("custom_modifier_") && Array.isArray(value) && value.length > 0) {
            const modifierName = key.replace("custom_modifier_", "")
            modifierCustomIds[modifierName] = value as number[]
        }
    })

    return {
        id: laborRate.id,
        row_order: laborRate.row_order,
        name: laborRate.name,
        time_type: laborRate.time_type,
        base_rate: laborRate.base_rate,
        custom_rates: laborRate.custom_rates ?? undefined,
        trades: laborRate.trades ?? [],
        classifications: laborRate.classifications ?? [],
        modifier_equipment_ids: laborRate.equipment_modifiers,
        modifier_custom_ids: Object.keys(modifierCustomIds).length > 0 ? modifierCustomIds : undefined,
        union_ids: laborRate.unions ?? [],
        cohort_ids: laborRate.cohorts ?? [],
        custom_fields: flattenCustomFields(laborRate.custom_fields),
        certification_ids: laborRate.certifications,
        license_ids: laborRate.licenses,
        project_client_names: laborRate.project_client_names ?? [],
    }
}

/**
 * Transforms an EquipmentRate from a cached RateTable into the format expected by the
 * matching library.
 *
 * @param equipmentRate - The equipment rate from the Redux entity cache
 */
const transformEquipmentRateForMatching = (equipmentRate: NestedEquipmentRate): SerializedEquipmentRate => {
    return {
        id: equipmentRate.id,
        row_order: equipmentRate.row_order,
        name: equipmentRate.name,
        base_rate: equipmentRate.base_rate,
        custom_rates: equipmentRate.custom_rates ?? undefined,
        statuses: equipmentRate.statuses ?? undefined,
        overtime_rate: equipmentRate.overtime_rate ?? undefined,
        idle_rate: equipmentRate.idle_rate ?? undefined,
        equipment: equipmentRate.equipment ?? [],
        categories: equipmentRate.categories ?? [],
        custom_fields: flattenCustomFields(equipmentRate.custom_fields),
    }
}

/**
 * Transforms a MaterialRate from a cached RateTable into the format expected by the
 * matching library.
 *
 * @param materialRate - The material rate from the Redux entity cache
 */
const transformMaterialRateForMatching = (materialRate: NestedMaterialRate): SerializedMaterialRate => {
    return {
        id: materialRate.id,
        row_order: materialRate.row_order,
        name: materialRate.name,
        base_rate: materialRate.base_rate,
        custom_rates: materialRate.custom_rates ?? undefined,
        groups: materialRate.groups ?? [],
        part_numbers: materialRate.part_numbers ?? [],
        materials: materialRate.materials ?? [],
        custom_fields: flattenCustomFields(materialRate.custom_fields),
    }
}

/**
 * Transforms a cached RateTable from Redux into the format expected by the matching library.
 * Includes labor, equipment, and material rates.
 *
 * @param rateTable - The rate table from the Redux entity cache
 */
const transformRateTableForMatching = (rateTable: RateTable): SerializedRateTable => {
    return {
        id: rateTable.id,
        name: rateTable.name,
        version: rateTable.version,
        company_id: rateTable.company_id,
        start_date: rateTable.start_date ?? undefined,
        end_date: rateTable.end_date ?? undefined,
        labor_rates: rateTable.labor_rates?.map(transformLaborRateForMatching) ?? [],
        equipment_rates: rateTable.equipment_rates?.map(transformEquipmentRateForMatching) ?? [],
        material_rates: rateTable.material_rates?.map(transformMaterialRateForMatching) ?? [],
    }
}

/**
 * Returns a map of rate table IDs to serialized rate tables, like this:
 * {
 *    <id>: <SerializedRateTable>
 * }
 *
 * Custom field criteria on labor rates are flattened from the nested structure
 * (keyed by model type) into flat CustomFieldCriterion[] arrays using
 * {@link flattenCustomFields} from rmbx_matching.
 */
export const selectRateTablesForMatchingById = createSelector(
    [selectRateTablesById],
    (rateTables): Partial<Record<number, SerializedRateTable>> => {
        return Object.values(rateTables).reduce((acc, rateTable) => {
            acc[rateTable.id] = transformRateTableForMatching(rateTable)
            return acc
        }, {} as Partial<Record<number, SerializedRateTable>>)
    }
)
